import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, RefreshCw, ShieldAlert, ShieldCheck, ShieldQuestion, PlayCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import api from '../services/api';
import { toast } from 'sonner';
import { formatMoney } from '../lib/money';

const TABS = ['pending', 'approved', 'rejected', 'all'];

const RiskBadge = ({ review }) => {
  if (!review) return <Badge className="bg-slate-100 text-slate-500 border-0">AI: no verdict</Badge>;
  const s = review.risk_score ?? 50;
  const cls = s >= 70 ? 'bg-rose-100 text-rose-700' : s >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';
  const Icon = s >= 70 ? ShieldAlert : s >= 40 ? ShieldQuestion : ShieldCheck;
  return (
    <Badge className={`${cls} border-0 inline-flex items-center gap-1`}>
      <Icon className="h-3 w-3" /> AI risk {s}/100 · {(review.verdict || '').replace(/_/g, ' ')}
    </Badge>
  );
};

export default function MarketplaceListingsPage() {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/admin/marketplace-listings', { params: { status_f: tab } })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tab]);

  const decide = async (id, action) => {
    let reason;
    if (action === 'reject') {
      reason = window.prompt('Reason for rejection (shown to the seller):') || '';
    }
    try {
      await api.post(`/admin/marketplace-listings/${id}/decision`, { action, reason });
      toast.success(action === 'approve' ? 'Listing approved — it is now live.' : 'Listing rejected.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Marketplace listings</h1>
          <p className="text-slate-500 text-sm mt-1">AI screens every listing for fakes — you make the final call.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="mkt-admin-refresh">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="mt-4 flex gap-1.5">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border capitalize ${tab === t ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}
            data-testid={`mkt-admin-tab-${t}`}>
            {t}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10 text-center text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500">No {tab === 'all' ? '' : tab} listings.</div>
      ) : (
        <div className="mt-5 space-y-4">
          {rows.map((l) => (
            <div key={l.listing_id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`mkt-admin-row-${l.listing_id}`}>
              <div className="flex flex-wrap gap-4">
                <div className="flex gap-2">
                  {(l.photos || []).slice(0, 3).map((p, i) => (
                    <a key={i} href={p} target="_blank" rel="noreferrer">
                      <img src={p} alt="" className="h-20 w-20 rounded-lg object-cover border border-slate-200" />
                    </a>
                  ))}
                  {l.video && (
                    <a href={l.video} target="_blank" rel="noreferrer"
                      className="h-20 w-20 rounded-lg bg-slate-900 text-white flex flex-col items-center justify-center text-[10px]" data-testid={`mkt-admin-video-${l.listing_id}`}>
                      <PlayCircle className="h-5 w-5" /> Video
                    </a>
                  )}
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-900">{l.title}</span>
                    <span className="text-emerald-700 font-semibold">{formatMoney(l.price, l.currency)}</span>
                    <Badge className="bg-slate-100 text-slate-600 border-0 capitalize">{l.status}</Badge>
                    <RiskBadge review={l.ai_review} />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {l.category} · {l.city} · seller: {l.seller_name} · {l.created_at ? new Date(l.created_at).toLocaleString() : ''}
                  </p>
                  <p className="text-sm text-slate-600 mt-1.5 line-clamp-2">{l.description}</p>
                  {l.ai_review?.reasons?.length > 0 && (
                    <ul className="mt-2 text-xs text-slate-500 list-disc pl-4 space-y-0.5" data-testid={`mkt-admin-reasons-${l.listing_id}`}>
                      {l.ai_review.reasons.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  )}
                  {l.rejection_reason && <p className="text-xs text-rose-600 mt-1">Rejected: {l.rejection_reason}</p>}
                </div>
                <div className="flex flex-col gap-2 justify-center">
                  {l.status !== 'approved' && (
                    <Button size="sm" onClick={() => decide(l.listing_id, 'approve')}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`mkt-approve-${l.listing_id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
                    </Button>
                  )}
                  {l.status !== 'rejected' && (
                    <Button size="sm" variant="outline" onClick={() => decide(l.listing_id, 'reject')}
                      className="border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`mkt-reject-${l.listing_id}`}>
                      <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
